import { recordedBreakMinutes } from 'src/utils/recordedBreak';
import BoundClientRateSelect from '../BoundClientRateSelect';
import { adminActionButtonSx } from 'src/theme/adminActionButton';
import AdminModal from 'src/components/AdminModal';
import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { ErrorMessage } from 'formik';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs as AdapterDayjsBasic } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider as LocalizationProviderBasic } from '@mui/x-date-pickers/LocalizationProvider';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import { deleteShift } from 'src/helper/backend_helper';

// Validation schema using Yup
const ShiftValidationSchema = Yup.object({
  // shiftNumber: Yup.string().required('Shift Number is required'),
  startDate: Yup.date().required('Start Date is required'),
  endDate: Yup.date().required('End Date is required'),
  position: Yup.string().required('Position is required'),
  quantity: Yup.number()
    .positive('Quantity must be positive')
    .required('Quantity is required'),
  startTime: Yup.string().required('Start Time is required'),
  endTime: Yup.string().required('End Time is required'),
  notes: Yup.string(),
  rateType: Yup.string(),
  callType: Yup.string().required('Call Type is required'),
  mealBreak: Yup.string().required('Meal Break is required'),
  rate: Yup.number()
    .min(0, 'Client Rate must be 0 or greater')
    .required('Client Rate is required'),
  desiredRate: Yup.number()
    .min(0, 'Desired Rate must be 0 or greater')
    .required('Desired Rate is required'),
  registerHours: Yup.number()
    .min(0, 'DoubleTime Hours must be 0 or greater')
    .required('Regular Hours is required'),
  overTimeHours: Yup.number()
    .min(0, 'DoubleTime Hours must be 0 or greater')
    .required('OverTime Hours is required'),
  DoubleTimeHours: Yup.number()
    .min(0, 'DoubleTime Hours must be 0 or greater')
    .required('DoubleTime Hours is required'),
  addOns: Yup.number()
    .nullable() // Allows null values
    .transform((value, originalValue) => (originalValue === '' ? null : value)) // Converts empty string to null
    .optional() // Keeps it optional
});

const EditShiftForm = ({
  shift,
  shiftOpen,
  handleCloseShift,
  onSubmit,
  onDelete,
  positionsArray,
  rateTypeOptions = [] as import('../updateQuote/savedQuoteRateTypes').SavedQuoteRateTypeOption[]
}) => {
  const [loading, setLoading] = useState(false);
  const [positionInput, setPositionInput] = React.useState('');

  const allPositions = useMemo(() => {
    return (
      positionsArray?.data?.data?.flatMap((category) =>
        category.positions.map((pos) => ({
          ...pos,
          category: (category.name || '').trim().toUpperCase()
        }))
      ) || []
    );
  }, [positionsArray]);

  // Calculate hours between times
  // const calculateHours = (startTime, endTime) => {
  //   if (!startTime || !endTime) return 0;

  //   const [startHour, startMinute] = startTime.split(':').map(Number);
  //   const [endHour, endMinute] = endTime.split(':').map(Number);

  //   let hours = endHour - startHour;
  //   const minutes = endMinute - startMinute;

  //   // Handle overnight shifts
  //   if (hours < 0) {
  //     hours += 24;
  //   }

  //   const total = (hours + minutes / 60).toFixed(2);

  //   return Number(total);
  // };

  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    let hours = endHour - startHour;
    const minutes = endMinute - startMinute;

    // Handle overnight shifts
    if (hours < 0) {
      hours += 24;
    }
    // const total = (hours + minutes / 60).toFixed(2);

    let startTotalMinutes = startHour * 60 + startMinute;
    let endTotalMinutes = endHour * 60 + endMinute;

    // Handle overnight shifts
    if (endTotalMinutes < startTotalMinutes) {
      endTotalMinutes += 24 * 60;
    }

    const totalMinutes = endTotalMinutes - startTotalMinutes;
    const remainingMinutes = totalMinutes % 60;

    return remainingMinutes < 45 && remainingMinutes > 0 ? hours + 1 : hours;
    // return Number(total);
  };

  const formik = useFormik({
    enableReinitialize: true, // This allows reinitialization when `shift` changes

    initialValues: {
      shiftNumber: shift?.shiftNumber || '',
      startDate: shift?.startDate
        ? new Date(shift?.startDate).toISOString().slice(0, 10)
        : null,
      endDate: shift?.endDate
        ? new Date(shift?.endDate).toISOString().slice(0, 10)
        : null,
      position: shift?.position || '',
      quantity: shift?.quantity || 0,
      availableQuantity: shift?.availableQuantity || 0,
      startTime: shift?.startTime || '08:00',
      endTime: shift?.endTime || '19:00',
      notes: shift?.notes || '',
      rateType: shift?.rateType || '',
      callType: shift?.callType || 'Show Day',
      mealBreak: shift?.mealBreak || '1hr Walk',
      rate: shift?.rate || 0,
      desiredRate: shift?.desiredRate || 0,
      calculatedHours: shift?.calculatedHours || 10,
      registerHours: shift?.registerHours || 0,
      overTimeHours: shift?.overTimeHours || 0,
      DoubleTimeHours: shift?.DoubleTimeHours || 0,
      addOns: shift?.addOns || 0,
      totals: shift?.totals || 0
    },
    validationSchema: ShiftValidationSchema,
    onSubmit: async (values) => {
      // const calculatedHours = Number(values.calculatedHours);
      // const registerHours = Number(values.registerHours);
      // const overtimeHours = Number(values.overTimeHours);
      // const doubleTimeHours = Number(values.DoubleTimeHours);

      const startDate = dayjs(values.startDate);
      const endDate = dayjs(values.endDate);
      if (startDate.isValid() && endDate.isValid()) {
        const diffInDays = endDate.diff(startDate, 'day'); // This is a number

        if (diffInDays === 0 || diffInDays === 1) {
          // Valid: same day or consecutive
        } else {
          toast.error('Date must be the same day or over night');
          return;
        }
      }

      // if (calculatedHours !== registerHours + overtimeHours + doubleTimeHours) {
      //   // isValid = false;
      //   toast.error(
      //     `Calculated Hours should be equal to regularHours + overtimeHours + DoubleTimeHours`
      //   );
      // } else {
      const result = await Swal.fire({
        title: 'Are you sure to update shift?',
        text: '',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, update it',
        didOpen: () => {
          const popup = document.querySelector('.swal2-container');
          if (popup) {
            (popup as HTMLElement).style.zIndex = '99999';
          }
        }
      });
      if (result.isConfirmed) {
        onSubmit({ ...values, id: shift.id });
        handleCloseShift();
      }
    }
    // }
  });

  // Update calculated hours whenever start or end time changes
  React.useEffect(() => {
    let calculatedHours = calculateHours(
      formik.values.startTime,
      formik.values.endTime
    );

    console.log(formik.values.mealBreak);

    calculatedHours = Math.max(0, calculatedHours - recordedBreakMinutes(formik.values.mealBreak) / 60);

    formik.setFieldValue('calculatedHours', calculatedHours);

    // Calculate totals based on rates and hours
    const regTotal = formik.values.rate * formik.values.registerHours;
    const otTotal = formik.values.rate * 1.5 * formik.values.overTimeHours;
    const dtTotal = formik.values.rate * 2 * formik.values.DoubleTimeHours;
    const total = regTotal + otTotal + dtTotal + formik.values.addOns;

    formik.setFieldValue('totals', total * formik.values.quantity);
    const available = formik.values.quantity - shift?.quantity;
    const availableQuantity = shift?.availableQuantity + available;
    console.log(availableQuantity);

    formik.setFieldValue('availableQuantity', availableQuantity);
  }, [
    formik.values.startTime,
    formik.values.endTime,
    formik.values.rate,
    formik.values.registerHours,
    formik.values.overTimeHours,
    formik.values.DoubleTimeHours,
    formik.values.addOns,
    formik.values.quantity,
    formik.values.mealBreak
  ]);

  const shouldDisableDate = (date: Dayjs, selectedRange) => {
    if (!selectedRange[0]) return false; // If no start date is selected, enable all dates

    const startDate = selectedRange[0];

    // Allow only the start date and the next day
    return (
      !date.isSame(startDate, 'day') &&
      !date.isSame(startDate.add(1, 'day'), 'day')
    );
  };

  const handleDeleteShift = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      willOpen: () => {
        const swalContainer = document.querySelector(
          '.swal2-container'
        ) as HTMLElement;
        if (swalContainer) {
          swalContainer.style.zIndex = '9999';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await deleteShift(shift.id);
          onDelete(shift.id);
          handleCloseShift();
          setLoading(false);
          window.location.reload();
        } catch (error) {
          setLoading(false);
          if (
            error?.response?.status >= 400 &&
            error?.response?.status <= 499
          ) {
            return toast.error(error?.response?.data?.message);
          }
          toast.error(error?.message);
        }
      }
    });
  };

  return (
    <AdminModal
      open={shiftOpen}
      dialogTitle="Edit Shift"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Stack>
            <Stack
              sx={{ display: 'flex', gap: 1, mb: 2 }}
              direction={{ sm: 'column', md: 'row' }}
            >
              <TextField
                focused
                fullWidth
                id="startDate"
                name="startDate"
                label="Shift Start Date"
                size="small"
                type="date"
                value={formik.values.startDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.startDate && Boolean(formik.errors.startDate)
                }
                helperText={
                  formik.touched.startDate && formik.errors.startDate ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.startDate}
                    </span>
                  ) : null
                }
              />
              <TextField
                focused
                fullWidth
                id="endDate"
                name="endDate"
                label="Shift End Date"
                size="small"
                type="date"
                value={formik.values.endDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={
                  formik.touched.endDate && formik.errors.endDate ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.endDate}
                    </span>
                  ) : null
                }
              />

              <Autocomplete
                size="small"
                fullWidth
                options={allPositions}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  return option.id === value.id || option.name === value.name;
                }}
                value={
                  allPositions.find(
                    (pos) => pos.name === formik.values.position
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    'position',
                    newValue ? newValue.name : ''
                  );
                }}
                inputValue={positionInput}
                onInputChange={(_, newInputValue) => {
                  setPositionInput(newInputValue);
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;

                  const searchTerms = inputValue.toLowerCase().split(/\s+/);

                  return options
                    .filter((option) => {
                      const positionName = (option.name || '').toLowerCase();
                      const categoryName = (option.category || '').toLowerCase();

                      return searchTerms.every(
                        (term) =>
                          positionName.includes(term) ||
                          categoryName.includes(term)
                      );
                    })
                    .sort((a, b) => {
                      const aName = (a.name || '').toLowerCase();
                      const bName = (b.name || '').toLowerCase();
                      const lowerInput = inputValue.toLowerCase();

                      const aExact = aName === lowerInput;
                      const bExact = bName === lowerInput;
                      if (aExact && !bExact) return -1;
                      if (!aExact && bExact) return 1;

                      const aStarts = aName.startsWith(lowerInput);
                      const bStarts = bName.startsWith(lowerInput);
                      if (aStarts && !bStarts) return -1;
                      if (!aStarts && bStarts) return 1;

                      return 0;
                    });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position"
                    error={
                      formik.touched.position && Boolean(formik.errors.position)
                    }
                    helperText={
                      formik.touched.position && formik.errors.position
                    }
                  />
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <ListSubheader
                      sx={{
                        fontWeight: 'bold',
                        color: 'black',
                        backgroundColor: '#f5f5f5',
                        lineHeight: '32px',
                        zIndex: 10 // Ensure it stays above items
                      }}
                    >
                      {params.group}
                    </ListSubheader>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: '500px',
                    '& .MuiAutocomplete-option': {
                      paddingLeft: '24px'
                    }
                  }
                }}
              />

              <TextField
                fullWidth
                id="quantity"
                name="quantity"
                label="Quantity"
                size="small"
                type="number"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.quantity && Boolean(formik.errors.quantity)
                }
                helperText={
                  formik.touched.quantity && formik.errors.quantity ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.quantity}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />
            </Stack>
            <Stack
              sx={{ display: 'flex', gap: 1, mb: 2 }}
              direction={{ sm: 'column', md: 'row' }}
            >
              <TextField
                id="startTime"
                name="startTime"
                type="time"
                label="Start Time"
                fullWidth
                size="small"
                value={formik.values.startTime}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.startTime && Boolean(formik.errors.startTime)
                }
                helperText={formik.touched.startTime && formik.errors.startTime}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                id="endTime"
                name="endTime"
                type="time"
                label="End Time"
                fullWidth
                size="small"
                value={formik.values.endTime}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.endTime && Boolean(formik.errors.endTime)}
                helperText={formik.touched.endTime && formik.errors.endTime}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes"
                size="small"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />

              <FormControl
                size="small"
                fullWidth
                error={
                  formik.touched.rateType && Boolean(formik.errors.rateType)
                }
              >
                <InputLabel id="rateType-label">Rate Type</InputLabel>
                <BoundClientRateSelect value={formik.values.rateType} onChange={formik.handleChange} options={rateTypeOptions} />
                {formik.touched.rateType && formik.errors.rateType && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: '3px',
                      marginLeft: '14px'
                    }}
                  >
                    {formik.errors.rateType}
                  </div>
                )}
              </FormControl>
            </Stack>
            <Stack
              sx={{ display: 'flex', gap: 1, mb: 2 }}
              direction={{ sm: 'column', md: 'row' }}
            >
              <FormControl
                size="small"
                fullWidth
                error={
                  formik.touched.callType && Boolean(formik.errors.callType)
                }
              >
                <InputLabel id="callType-label">Call Type</InputLabel>
                <Select
                  labelId="callType-label"
                  id="callType"
                  name="callType"
                  label="Call Type"
                  value={formik.values.callType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="Setup">Setup</MenuItem>
                  <MenuItem value="Strike">Strike</MenuItem>
                  <MenuItem value="Show Day">Show Day</MenuItem>
                  <MenuItem value="Set & Show">Set & Show</MenuItem>
                  <MenuItem value="Show & Strike">Show & Strike</MenuItem>
                  <MenuItem value="Rehearsal">Rehearsal</MenuItem>
                  <MenuItem value="Set/Show/Strike">Set/Show/Strike</MenuItem>
                </Select>
                {formik.touched.callType && formik.errors.callType && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: '3px',
                      marginLeft: '14px'
                    }}
                  >
                    {formik.errors.callType}
                  </div>
                )}
              </FormControl>

              <FormControl
                size="small"
                fullWidth
                error={
                  formik.touched.mealBreak && Boolean(formik.errors.mealBreak)
                }
              >
                <InputLabel id="mealBreak-label">Meal Break</InputLabel>
                <Select
                  labelId="mealBreak-label"
                  id="mealBreak"
                  name="mealBreak"
                  label="Meal Break"
                  value={formik.values.mealBreak}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="1hr Walk">1hr Walk</MenuItem>
                  <MenuItem value="2hr Walk">2hr walk</MenuItem>{' '}
                  <MenuItem value="TBD">TBD</MenuItem>
                  <MenuItem value="30 min">30 min</MenuItem>
                  <MenuItem value="Skipped">Skipped</MenuItem>
                  <MenuItem value="MP">MP</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
                {formik.touched.mealBreak && formik.errors.mealBreak && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: '3px',
                      marginLeft: '14px'
                    }}
                  >
                    {formik.errors.mealBreak}
                  </div>
                )}
              </FormControl>

              <TextField
                fullWidth
                id="clientRate"
                name="rate"
                label="Client Rate($)"
                size="small"
                type="number"
                value={formik.values.rate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.rate && Boolean(formik.errors.rate)}
                helperText={
                  formik.touched.rate && formik.errors.rate ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.rate}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />

              <TextField
                fullWidth
                id="desiredRate"
                name="desiredRate"
                label="Desired Rate($)"
                size="small"
                type="number"
                value={formik.values.desiredRate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.desiredRate &&
                  Boolean(formik.errors.desiredRate)
                }
                helperText={
                  formik.touched.desiredRate && formik.errors.desiredRate ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.desiredRate}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />
            </Stack>

            <Stack
              sx={{ display: 'flex', gap: 1, mb: 2 }}
              direction={{ sm: 'column', md: 'row' }}
            >
              <TextField
                disabled
                fullWidth
                id="calculatedHours"
                name="calculatedHours"
                label="Calculated Hrs"
                size="small"
                type="number"
                value={formik.values.calculatedHours}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                id="registerHours"
                name="registerHours"
                label="ST Hrs"
                size="small"
                type="number"
                // value={formik.values.registerHours}
                // onChange={formik.handleChange}

                value={
                  formik.values.registerHours === 0
                    ? ''
                    : formik.values.registerHours
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    // user cleared the field, so keep the Formik value at 0
                    formik.setFieldValue('registerHours', 0);
                  } else {
                    // otherwise, parse to a number
                    formik.setFieldValue('registerHours', Number(val));
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.registerHours &&
                  Boolean(formik.errors.registerHours)
                }
                helperText={
                  formik.touched.registerHours &&
                  formik.errors.registerHours ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.registerHours}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />

              <TextField
                fullWidth
                id="overTimeHours"
                name="overTimeHours"
                label="OT Hrs"
                size="small"
                type="number"
                // value={formik.values.overTimeHours}
                // onChange={formik.handleChange}

                value={
                  formik.values.overTimeHours === 0
                    ? ''
                    : formik.values.overTimeHours
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    // user cleared the field, so keep the Formik value at 0
                    formik.setFieldValue('overTimeHours', 0);
                  } else {
                    // otherwise, parse to a number
                    formik.setFieldValue('overTimeHours', Number(val));
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.overTimeHours &&
                  Boolean(formik.errors.overTimeHours)
                }
                helperText={
                  formik.touched.overTimeHours &&
                  formik.errors.overTimeHours ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.overTimeHours}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />
              <TextField
                fullWidth
                id="DoubleTimeHours"
                name="DoubleTimeHours"
                label="DT Hrs"
                size="small"
                type="number"
                // value={formik.values.DoubleTimeHours}
                // onChange={formik.handleChange}

                value={
                  formik.values.DoubleTimeHours === 0
                    ? ''
                    : formik.values.DoubleTimeHours
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    // user cleared the field, so keep the Formik value at 0
                    formik.setFieldValue('DoubleTimeHours', 0);
                  } else {
                    // otherwise, parse to a number
                    formik.setFieldValue('DoubleTimeHours', Number(val));
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.DoubleTimeHours &&
                  Boolean(formik.errors.DoubleTimeHours)
                }
                helperText={
                  formik.touched.DoubleTimeHours &&
                  formik.errors.DoubleTimeHours ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.DoubleTimeHours}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />
            </Stack>
            <Stack
              sx={{ display: 'flex', gap: 1, mb: 2 }}
              direction={{ sm: 'column', md: 'row' }}
            >
              <TextField
                // fullWidth
                sx={{ width: '24%' }}
                id="outlined-error-helper-text"
                name="addOns"
                label="Per Diem"
                size="small"
                type="number"
                value={formik.values.addOns}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.addOns && Boolean(formik.errors.addOns)}
                helperText={
                  formik.touched.addOns && formik.errors.addOns ? (
                    <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                      {formik.errors.addOns}
                    </span>
                  ) : null
                }
                InputProps={{
                  inputProps: {
                    style: {
                      appearance: 'textfield',
                      MozAppearance: 'textfield'
                    }
                  },
                  sx: {
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      }
                  }
                }}
              />

              <TextField
                disabled
                // fullWidth
                id="outlined-error-helper-text"
                label="Totals"
                size="small"
                value={formik.values.totals}
              />
            </Stack>
          </Stack>
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}
            >
              <Button
                type="submit"
                variant="contained"
                sx={{
                  width: 100,
                  ...adminActionButtonSx
                }}
              >
                Update
              </Button>
              <Button
                variant="outlined"
                sx={{
                  width: 100,
                  borderColor: '#E8BF21',
                  borderWidth: 2,
                  color: '#000000',
                  '&:hover': {
                    borderColor: '#E8BF21',
                    borderWidth: 2,
                    backgroundColor: 'none'
                  }
                }}
                onClick={handleCloseShift}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: 100,

                  backgroundColor: 'red',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'red'
                  }
                }}
                onClick={handleDeleteShift}
              >
                Delete
              </Button>
            </Box>
          )}
        </form>
      </Box>
    </AdminModal>
  );
};
export default EditShiftForm;
